// ResearchDocsSlice_Store.ts
//
// Selection state + detail cache for the inline research-docs table.
// Mirrors DealsTableSlice so reviewers familiar with one can read the other.
//
// The detail-fetch endpoint is stubbed for PR 1 — swap `DETAIL_BASE_URL`
// + the path template once backend confirms the research-doc detail route.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { ResearchDocsSliceState, ResearchDocDetail } from "./types/redux";
import { authenticatedFetch } from "./utils/authErrorHandler";

// TODO(RD-8): replace stub URL once backend confirms the research-doc
// detail endpoint. Kept as a separate constant so the swap is one line.
// `||` (not `??`) intentionally — an empty-string env var should also fall
// through to the deals base URL rather than resolve to a relative path.
const DETAIL_BASE_URL =
  import.meta.env.VITE_RESEARCH_DOCS_API_BASE_URL
  || import.meta.env.VITE_DEALS_API_BASE_URL;

// --- Async thunk: fetch full research doc details from API ---

export const fetchResearchDocDetail = createAsyncThunk<
  { docId: string; data: ResearchDocDetail },
  string,
  { state: { researchDocsSlice: ResearchDocsSliceState }; rejectValue: string }
>(
  "researchDocs/fetchResearchDocDetail",
  async (docId, { getState, rejectWithValue }) => {
    // Skip fetch if already cached
    const cached = getState().researchDocsSlice.detailCache[docId];
    if (cached) {
      return { docId, data: cached.data };
    }

    const response = await authenticatedFetch(
      `${DETAIL_BASE_URL}/api/objects/research-docs/${docId}`
    );

    if (!response.ok) {
      return rejectWithValue(
        `Failed to fetch research doc details: ${response.status} ${response.statusText}`
      );
    }

    const data: ResearchDocDetail = await response.json();
    return { docId, data };
  }
);

// --- Slice ---

const initialState: ResearchDocsSliceState = {
  selectedDocId: null,
  detailCache: {},
  detailLoading: null,
  detailError: null,
};

const researchDocsSlice = createSlice({
  name: "researchDocs",
  initialState,
  reducers: {
    openResearchDoc: (state, action: PayloadAction<string>) => {
      state.selectedDocId = action.payload;
      state.detailError = null;
    },
    closeResearchDoc: (state) => {
      state.selectedDocId = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResearchDocDetail.pending, (state, action) => {
        state.detailLoading = action.meta.arg;
        state.detailError = null;
      })
      .addCase(fetchResearchDocDetail.fulfilled, (state, action) => {
        const { docId, data } = action.payload;
        state.detailCache[docId] = {
          data,
          fetchedAt: Date.now(),
        };
        if (state.detailLoading === docId) {
          state.detailLoading = null;
        }
      })
      .addCase(fetchResearchDocDetail.rejected, (state, action) => {
        const docId = action.meta.arg;
        if (state.detailLoading === docId) {
          state.detailLoading = null;
          state.detailError =
            action.payload ?? action.error.message ?? "Unknown error";
        }
      });
  },
});

export const {
  openResearchDoc,
  closeResearchDoc,
} = researchDocsSlice.actions;

// --- Selectors ---

export const selectSelectedResearchDocId = (state: {
  researchDocsSlice: ResearchDocsSliceState;
}): string | null => state.researchDocsSlice.selectedDocId;

export const selectResearchDocDetail = (
  state: { researchDocsSlice: ResearchDocsSliceState },
  docId: string | null
): ResearchDocDetail | undefined => {
  if (!docId) return undefined;
  return state.researchDocsSlice.detailCache[docId]?.data;
};

export const selectResearchDocDetailLoading = (state: {
  researchDocsSlice: ResearchDocsSliceState;
}): string | null => state.researchDocsSlice.detailLoading;

export const selectResearchDocDetailError = (state: {
  researchDocsSlice: ResearchDocsSliceState;
}): string | null => state.researchDocsSlice.detailError;

export default researchDocsSlice.reducer;
